import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
} from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiClientError } from "../api.js";
import type { AssetType, BrandAsset, BrandProfile, Client } from "../types.js";
import { displayWebsite } from "../url-display.js";

type Tab = "overview" | "brand" | "files" | "settings";
const tabs: Array<{ id: Tab; label: string }> = [
  { id: "overview", label: "Обзор" },
  { id: "brand", label: "Фирменный стиль" },
  { id: "files", label: "Файлы" },
  { id: "settings", label: "Настройки" },
];

export function ClientDetailsScreen() {
  const { clientId = "" } = useParams();
  const [client, setClient] = useState<Client | null>(null);
  const [profile, setProfile] = useState<BrandProfile | null>(null);
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [tab, setTab] = useState<Tab>("overview");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [nextClient, nextProfile, nextAssets] = await Promise.all([
        api.getClient(clientId),
        api.getBrandProfile(clientId),
        api.listAssets(clientId),
      ]);
      setClient(nextClient);
      setProfile(nextProfile);
      setAssets(nextAssets.items);
      setError("");
    } catch {
      setError("Не удалось загрузить данные клиента.");
    }
  };
  useEffect(() => {
    void load();
  }, [clientId]);
  if (error)
    return (
      <section className="panel detail-loading form-error">{error}</section>
    );
  if (!client || !profile)
    return (
      <section className="panel detail-loading">Загрузка клиента…</section>
    );

  return (
    <section className="client-details">
      <div className="panel detail-header">
        <div className="detail-title">
          <div>
            <Link to="/clients">← Клиенты</Link>
            <div>
              <i>{client.name.slice(0, 2).toUpperCase()}</i>
              <div>
                <h1>{client.name}</h1>
                <p>
                  {client.industry || "Отрасль не указана"} ·{" "}
                  {client.assetCount} файлов
                </p>
              </div>
            </div>
          </div>
          <div className="detail-header-actions">
            <span className={`status-pill ${client.status}`}>
              {client.status === "active" ? "Активен" : "В архиве"}
            </span>
            <Link
              className="button primary compact-button"
              to={`/create?clientId=${client.id}`}
            >
              ＋ Создать изображение
            </Link>
          </div>
        </div>
        <div className="detail-tabs" role="tablist">
          {tabs.map((item) => (
            <button
              role="tab"
              aria-selected={tab === item.id}
              className={tab === item.id ? "active" : ""}
              key={item.id}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      {tab === "overview" ? <Overview client={client} assets={assets} /> : null}
      {tab === "brand" ? (
        <BrandForm profile={profile} onSaved={setProfile} />
      ) : null}
      {tab === "files" ? (
        <AssetsPanel
          clientId={client.id}
          assets={assets}
          onChanged={() => void load()}
        />
      ) : null}
      {tab === "settings" ? (
        <ClientSettings client={client} onSaved={setClient} />
      ) : null}
    </section>
  );
}

function Overview({
  client,
  assets,
}: {
  client: Client;
  assets: BrandAsset[];
}) {
  const preferredAssets = assets.filter(
    (asset) => asset.preferredForGeneration || asset.preferredForOverlay,
  );
  const keyAssets = (preferredAssets.length ? preferredAssets : assets).slice(
    0,
    6,
  );
  return (
    <div className="detail-grid">
      <section className="panel detail-section overview-main">
        <SectionTitle kicker="Профиль" title="О клиенте" />
        <Info
          label="Сайт"
          value={
            client.website ? (
              <a href={client.website} target="_blank" rel="noreferrer">
                {displayWebsite(client.website)}
              </a>
            ) : (
              "—"
            )
          }
        />
        <Info label="Отрасль" value={client.industry || "—"} />
        <Info label="Описание" value={client.description || "—"} />
        <Info label="Позиционирование" value={client.positioning || "—"} />
        <Info label="Целевая аудитория" value={client.targetAudience || "—"} />
        <Info
          label="Тон коммуникации"
          value={client.communicationTone || "—"}
        />
      </section>
      <aside className="panel detail-section">
        <SectionTitle kicker="Быстрый доступ" title="Ключевые материалы" />
        {keyAssets.length ? (
          <div className="key-assets">
            {keyAssets.map((asset) => (
              <img
                key={asset.id}
                src={asset.previewUrl}
                alt={asset.title}
                title={asset.title}
              />
            ))}
          </div>
        ) : (
          <div className="compact-empty">
            Отметьте рекомендуемые материалы во вкладке «Файлы».
          </div>
        )}
      </aside>
    </div>
  );
}

function BrandForm({
  profile,
  onSaved,
}: {
  profile: BrandProfile;
  onSaved: (profile: BrandProfile) => void;
}) {
  const [form, setForm] = useState(profile);
  const [message, setMessage] = useState("");
  useEffect(() => setForm(profile), [profile]);
  const update = <K extends keyof BrandProfile>(
    key: K,
    value: BrandProfile[K],
  ) => setForm((current) => ({ ...current, [key]: value }));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage("");
    try {
      const saved = await api.updateBrandProfile(profile.clientId, form);
      onSaved(saved);
      setMessage("Сохранено");
    } catch (reason) {
      setMessage(
        reason instanceof ApiClientError ? reason.message : "Ошибка сохранения",
      );
    }
  };
  return (
    <form
      className="panel detail-section brand-form"
      onSubmit={(event) => void submit(event)}
    >
      <div className="section-row">
        <SectionTitle
          kicker="Структурированный профиль"
          title="Фирменный стиль"
        />
        <button className="primary compact-button">Сохранить</button>
      </div>
      <div className="brand-columns">
        <div>
          <ColorEditor
            label="Основные цвета"
            values={form.primaryColors}
            onChange={(values) => update("primaryColors", values)}
          />
          <ColorEditor
            label="Дополнительные цвета"
            values={form.secondaryColors}
            onChange={(values) => update("secondaryColors", values)}
          />
          <TextArea
            label="Визуальный стиль"
            value={form.visualStyle}
            onChange={(value) => update("visualStyle", value)}
          />
          <TextArea
            label="Стиль фотографии"
            value={form.photographyStyle}
            onChange={(value) => update("photographyStyle", value)}
          />
          <TextArea
            label="Правила композиции"
            value={form.compositionRules}
            onChange={(value) => update("compositionRules", value)}
          />
        </div>
        <div>
          <TextArea
            label="Освещение"
            value={form.lightingPreferences}
            onChange={(value) => update("lightingPreferences", value)}
          />
          <ListInput
            label="Предпочтительные стили"
            values={form.preferredStyles}
            onChange={(value) => update("preferredStyles", value)}
          />
          <ListInput
            label="Запрещённые стили"
            values={form.forbiddenStyles}
            onChange={(value) => update("forbiddenStyles", value)}
          />
          <label>
            Уровень реализма{" "}
            <span className="range-value">{form.realismLevel}%</span>
            <input
              aria-label="Уровень реализма"
              type="range"
              min="0"
              max="100"
              value={form.realismLevel}
              onChange={(event) =>
                update("realismLevel", Number(event.target.value))
              }
            />
          </label>
          <TextArea
            label="Дополнительные инструкции"
            value={form.additionalBrandInstructions}
            onChange={(value) => update("additionalBrandInstructions", value)}
            rows={5}
          />
        </div>
      </div>
      {message ? (
        <div
          className={message === "Сохранено" ? "save-message" : "form-error"}
        >
          {message}
        </div>
      ) : null}
    </form>
  );
}

function ColorEditor({
  label,
  values,
  onChange,
}: {
  label: string;
  values: string[];
  onChange: (value: string[]) => void;
}) {
  const next = (index: number, value: string) =>
    onChange(
      values.map((current, position) =>
        position === index ? value.toUpperCase() : current,
      ),
    );
  return (
    <fieldset className="color-editor">
      <legend>{label}</legend>
      {values.map((value, index) => (
        <div className="color-row" key={`${index}-${value}`}>
          <input
            aria-label={`${label} color ${index + 1}`}
            type="color"
            value={/^#[0-9A-F]{6}$/u.test(value) ? value : "#000000"}
            onChange={(event) => next(index, event.target.value)}
          />
          <input
            aria-label={`${label} HEX ${index + 1}`}
            value={value}
            pattern="#[0-9A-Fa-f]{6}"
            onChange={(event) => next(index, event.target.value)}
          />
          <button
            type="button"
            onClick={() =>
              onChange(values.filter((_, position) => position !== index))
            }
          >
            ×
          </button>
        </div>
      ))}
      <button
        className="text-button"
        type="button"
        onClick={() => onChange([...values, "#000000"])}
      >
        ＋ Добавить цвет
      </button>
    </fieldset>
  );
}

function AssetsPanel({
  clientId,
  assets,
  onChanged,
}: {
  clientId: string;
  assets: BrandAsset[];
  onChanged: () => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [type, setType] = useState<AssetType>("reference");
  const [subtype, setSubtype] = useState("primary");
  const [generation, setGeneration] = useState(false);
  const [overlay, setOverlay] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const groups = useMemo(() => groupAssets(assets), [assets]);
  const choose = (list: FileList | null) => setFiles(list ? [...list] : []);
  const drop = (event: DragEvent) => {
    event.preventDefault();
    choose(event.dataTransfer.files);
  };
  const upload = async () => {
    if (!files.length) return;
    setUploading(true);
    setError("");
    try {
      for (const file of files)
        await api.uploadAsset(clientId, file, {
          type,
          subtype: type === "logo" ? subtype : "",
          title: file.name.replace(/\.[^.]+$/u, ""),
          preferredForGeneration: generation,
          preferredForOverlay: overlay,
        });
      setFiles([]);
      onChanged();
    } catch (reason) {
      setError(
        reason instanceof ApiClientError ? reason.message : "Ошибка загрузки",
      );
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="files-layout">
      <aside className="panel upload-panel">
        <SectionTitle kicker="PNG · JPEG · WebP" title="Загрузить материалы" />
        <label
          className="upload-drop"
          onDragOver={(event) => event.preventDefault()}
          onDrop={drop}
        >
          ⇧<strong>Перетащите файлы</strong>
          <small>или выберите на компьютере · до 10 MB</small>
          <input
            aria-label="Выбрать файлы"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              choose(event.target.files)
            }
          />
        </label>
        {files.length ? (
          <div className="selected-files">
            {files.map((file) => (
              <span key={`${file.name}-${file.size}`}>
                {file.name}
                <small>{formatBytes(file.size)}</small>
              </span>
            ))}
          </div>
        ) : null}
        <label>
          Тип
          <select
            aria-label="Тип материала"
            value={type}
            onChange={(event) => setType(event.target.value as AssetType)}
          >
            {assetTypeOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {type === "logo" ? (
          <label>
            Вариант логотипа
            <select
              aria-label="Подтип логотипа"
              value={subtype}
              onChange={(event) => setSubtype(event.target.value)}
            >
              <option value="primary">Основной</option>
              <option value="white">Белый</option>
              <option value="black">Чёрный</option>
              <option value="mark">Знак</option>
              <option value="watermark">Водяной знак</option>
              <option value="other">Другой</option>
            </select>
          </label>
        ) : null}
        <label className="check-row">
          <input
            type="checkbox"
            checked={generation}
            onChange={(event) => setGeneration(event.target.checked)}
          />{" "}
          Использовать для генерации
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={overlay}
            onChange={(event) => setOverlay(event.target.checked)}
          />{" "}
          Использовать для overlay
        </label>
        {error ? <div className="form-error">{error}</div> : null}
        <button
          className="primary compact-button centered"
          disabled={!files.length || uploading}
          onClick={() => void upload()}
        >
          {uploading
            ? "Загрузка…"
            : `Загрузить${files.length > 1 ? ` (${files.length})` : ""}`}
        </button>
      </aside>
      <section className="panel asset-library">
        <div className="section-row">
          <SectionTitle
            kicker="Библиотека"
            title={`Файлы · ${assets.length}`}
          />
        </div>
        {assets.length ? (
          Object.entries(groups).map(([group, items]) => (
            <div className="asset-group" key={group}>
              <h3>{group}</h3>
              <div className="asset-grid">
                {items.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    onChanged={onChanged}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="compact-empty large">Материалы ещё не загружены.</div>
        )}
      </section>
    </div>
  );
}

function AssetCard({
  asset,
  onChanged,
}: {
  asset: BrandAsset;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    title: asset.title,
    type: asset.type,
    subtype: asset.subtype ?? "primary",
  });
  const [error, setError] = useState("");
  useEffect(
    () =>
      setDraft({
        title: asset.title,
        type: asset.type,
        subtype: asset.subtype ?? "primary",
      }),
    [asset],
  );
  const toggle = (field: "preferredForGeneration" | "preferredForOverlay") =>
    void api.updateAsset(asset.id, { [field]: !asset[field] }).then(onChanged);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      await api.updateAsset(asset.id, {
        title: draft.title,
        type: draft.type,
        subtype: draft.type === "logo" ? draft.subtype : null,
      });
      setEditing(false);
      onChanged();
    } catch (reason) {
      setError(
        reason instanceof ApiClientError ? reason.message : "Ошибка сохранения",
      );
    }
  };
  return (
    <article className="asset-card">
      <div className="asset-preview">
        <img src={asset.previewUrl} alt={asset.title} />
        <span>
          {asset.width}×{asset.height}
        </span>
      </div>
      <div className="asset-info">
        <strong>{asset.title}</strong>
        <small>
          {asset.originalFilename} · {formatBytes(asset.fileSize)}
        </small>
        {editing ? (
          <form className="asset-editor" onSubmit={(event) => void save(event)}>
            <input
              aria-label={`Название ${asset.title}`}
              required
              maxLength={240}
              value={draft.title}
              onChange={(event) =>
                setDraft({ ...draft, title: event.target.value })
              }
            />
            <select
              aria-label={`Тип ${asset.title}`}
              value={draft.type}
              onChange={(event) =>
                setDraft({ ...draft, type: event.target.value as AssetType })
              }
            >
              {assetTypeOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {draft.type === "logo" ? (
              <select
                aria-label={`Подтип ${asset.title}`}
                value={draft.subtype}
                onChange={(event) =>
                  setDraft({ ...draft, subtype: event.target.value })
                }
              >
                {logoSubtypeOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            ) : null}
            <div>
              <button type="submit">Сохранить</button>
              <button type="button" onClick={() => setEditing(false)}>
                Отмена
              </button>
            </div>
            {error ? <span className="form-error">{error}</span> : null}
          </form>
        ) : null}
        <div className="asset-badges">
          <button
            className={asset.preferredForGeneration ? "active" : ""}
            onClick={() => toggle("preferredForGeneration")}
          >
            Для генерации
          </button>
          <button
            className={asset.preferredForOverlay ? "active" : ""}
            onClick={() => toggle("preferredForOverlay")}
          >
            Наложение
          </button>
          <a href={asset.downloadUrl} aria-label={`Скачать ${asset.title}`}>
            ↓
          </a>
          <button
            aria-label={`Изменить ${asset.title}`}
            onClick={() => setEditing((current) => !current)}
          >
            ✎
          </button>
          <button
            aria-label={`Архивировать ${asset.title}`}
            onClick={() => void api.archiveAsset(asset.id).then(onChanged)}
          >
            ×
          </button>
        </div>
      </div>
    </article>
  );
}

function ClientSettings({
  client,
  onSaved,
}: {
  client: Client;
  onSaved: (client: Client) => void;
}) {
  const [form, setForm] = useState(client);
  const [message, setMessage] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const saved = await api.updateClient(client.id, form);
    onSaved(saved);
    setMessage("Сохранено");
  };
  return (
    <form
      className="panel detail-section settings-form"
      onSubmit={(event) => void submit(event)}
    >
      <div className="section-row">
        <SectionTitle kicker="Параметры" title="Основные данные" />
        <button className="primary compact-button">Сохранить</button>
      </div>
      <div className="form-grid">
        <label>
          Название
          <input
            aria-label="Название клиента"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </label>
        <label>
          Сайт
          <input
            value={form.website ?? ""}
            onChange={(event) =>
              setForm({ ...form, website: event.target.value })
            }
          />
        </label>
        <label>
          Отрасль
          <input
            value={form.industry ?? ""}
            onChange={(event) =>
              setForm({ ...form, industry: event.target.value })
            }
          />
        </label>
        <label className="wide">
          Описание
          <textarea
            rows={4}
            value={form.description}
            onChange={(event) =>
              setForm({ ...form, description: event.target.value })
            }
          />
        </label>
        <label className="wide">
          Позиционирование
          <textarea
            rows={3}
            value={form.positioning}
            onChange={(event) =>
              setForm({ ...form, positioning: event.target.value })
            }
          />
        </label>
        <label className="wide">
          Целевая аудитория
          <textarea
            rows={3}
            value={form.targetAudience}
            onChange={(event) =>
              setForm({ ...form, targetAudience: event.target.value })
            }
          />
        </label>
        <label className="wide">
          Тон коммуникации
          <textarea
            rows={3}
            value={form.communicationTone}
            onChange={(event) =>
              setForm({ ...form, communicationTone: event.target.value })
            }
          />
        </label>
      </div>
      {message ? <div className="save-message">{message}</div> : null}
      <div className="danger-zone">
        <div>
          <strong>Архивировать клиента</strong>
          <small>
            Данные и материалы сохранятся для будущей истории генераций.
          </small>
        </div>
        <button
          type="button"
          disabled={client.status === "archived"}
          onClick={() => void api.archiveClient(client.id).then(onSaved)}
        >
          В архив
        </button>
      </div>
    </form>
  );
}

function SectionTitle({ title }: { kicker: string; title: string }) {
  return <h2>{title}</h2>;
}
function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="info-row">
      <span>{label}</span>
      <div>{value}</div>
    </div>
  );
}
function TextArea({
  label,
  value,
  onChange,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}) {
  return (
    <label>
      {label}
      <textarea
        aria-label={label}
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
function ListInput({
  label,
  values,
  onChange,
}: {
  label: string;
  values: string[];
  onChange: (value: string[]) => void;
}) {
  return (
    <label>
      {label}
      <input
        aria-label={label}
        value={values.join(", ")}
        onChange={(event) =>
          onChange(
            event.target.value
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean),
          )
        }
        placeholder="Через запятую"
      />
    </label>
  );
}
function formatBytes(bytes: number) {
  return bytes < 1024 * 1024
    ? `${Math.ceil(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
const assetTypeOptions: Array<[AssetType, string]> = [
  ["logo", "Логотип"],
  ["product", "Фото продукции"],
  ["style_reference", "Стилевой референс"],
  ["reference", "Референс"],
  ["background", "Фон"],
  ["person", "Люди"],
  ["other", "Другое"],
];
const logoSubtypeOptions = [
  ["primary", "Основной"],
  ["white", "Белый"],
  ["black", "Чёрный"],
  ["mark", "Знак"],
  ["watermark", "Водяной знак"],
  ["other", "Другой"],
] as const;
function groupAssets(assets: BrandAsset[]) {
  const labels: Record<AssetType, string> = {
    logo: "Логотипы",
    product: "Фото продукции",
    style_reference: "Стилевые референсы",
    reference: "Референсы",
    background: "Фоны",
    person: "Люди",
    other: "Другие",
  };
  return assets.reduce<Record<string, BrandAsset[]>>((groups, asset) => {
    (groups[labels[asset.type]] ??= []).push(asset);
    return groups;
  }, {});
}
